import { BRIDGE_URL, type BridgeState, type FromBridge, type ToBridge } from '@yurt/protocol';
import { parseBridgeMessage } from './bridgeMessages';
import { kv } from './db';
import { report } from './diagnostics';
import { errorText } from './format';
import { loadToken } from './stored';

export type BridgeStatus = 'off' | 'missing' | 'connecting' | 'unpaired' | 'connected';

type Listener = (status: BridgeStatus, state: BridgeState | null) => void;

/**
 * Talks to the local yurt-bridge. It only dials 127.0.0.1 once the user has asked for agents
 * (or has paired before), so visitors never see a local-network permission prompt out of nowhere.
 */
class BridgeClient {
  status: BridgeStatus = 'off';
  /** Where the bridge listens. Tests point it at a bridge on another port, so they never touch the user's own. */
  url = BRIDGE_URL;
  state: BridgeState | null = null;
  /** First redial delay (it backs off from there) and how long a pairing waits for an answer; tests shorten them. */
  retryMs = 2000;
  pairTimeoutMs = 8000;
  private ws: WebSocket | null = null;
  private token: string | null = null;
  private retries = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<Listener>();
  private waiters: ((m: FromBridge) => void)[] = [];
  private identity: { phrase: string; name: string; handle: string } | null = null;

  subscribe(l: Listener) {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }
  private set(s: BridgeStatus) {
    this.status = s;
    for (const l of this.listeners) l(this.status, this.state);
  }

  async autoStart(identity: { phrase: string; name: string; handle: string }) {
    this.identity = identity;
    this.token = loadToken(await kv.get('bridgeToken'));
    if (this.token) this.start();
  }

  setIdentity(identity: { phrase: string; name: string; handle: string }) {
    this.identity = identity;
    if (this.status === 'connected') this.send({ t: 'identity', ...identity });
  }

  start() {
    if (this.ws || this.timer) return;
    this.set(this.status === 'off' ? 'connecting' : this.status);
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      return this.later();
    }
    this.ws = ws;
    ws.onopen = () => {
      this.retries = 0;
      this.send(this.token ? { t: 'hello', token: this.token } : { t: 'hello' });
    };
    // Anything that isn't a known message (another program on the port, a newer bridge) is ignored.
    ws.onmessage = (e) => {
      const m = parseBridgeMessage(e.data);
      if (m) this.onMsg(m);
    };
    ws.onclose = () => {
      this.ws = null;
      this.state = null;
      this.set('missing');
      this.later();
    };
  }

  stop() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    // Detach first: a close event from this socket would otherwise schedule a redial.
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = null;
      ws.close();
    }
    this.state = null;
    this.set('off');
  }

  private later() {
    const delay = Math.min(this.retryMs * 1.6 ** this.retries++, 30000);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.start();
    }, delay);
  }

  private onMsg(m: FromBridge) {
    for (const w of [...this.waiters]) w(m);
    if (m.t === 'hello') {
      if (m.paired) {
        this.set('connected');
        if (this.identity) this.send({ t: 'identity', ...this.identity });
      } else this.set('unpaired');
    } else if (m.t === 'paired') {
      this.token = m.token;
      // Losing the token only means pairing again next time; record it rather than leave the rejection unhandled.
      kv.set('bridgeToken', m.token).catch((err: unknown) => report('bridge', 'error', 'Couldn’t save the pairing: ' + errorText(err)));
      this.set('connected');
      if (this.identity) this.send({ t: 'identity', ...this.identity });
    } else if (m.t === 'state') {
      // The bridge sends state only to paired browsers (after hello or pairing), so this session is connected.
      this.state = m.state;
      this.set('connected');
    }
  }

  send(m: ToBridge) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  pair(code: string): Promise<boolean> {
    return new Promise((res) => {
      // Every exit removes the waiter, so timed-out pairings don't pile up.
      const done = (ok: boolean) => {
        clearTimeout(timer);
        this.waiters = this.waiters.filter((x) => x !== w);
        res(ok);
      };
      const w = (m: FromBridge) => {
        if (m.t === 'paired' || m.t === 'error') done(m.t === 'paired');
      };
      const timer = setTimeout(() => done(false), this.pairTimeoutMs);
      this.waiters.push(w);
      this.send({ t: 'pair', code });
    });
  }

  async forget() {
    await kv.del('bridgeToken');
    this.token = null;
    this.stop();
  }
}

export const bridge = new BridgeClient();
